import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Truck, Package, FileText, ExternalLink, Save, Copy } from "lucide-react";
import { fmtTRY, fmtDate } from "@/lib/format";

/**
 * Tedarikçi Siparişi Detay Modalı.
 * Bir siparişin tüm alanlarını gösterir + kaynak teklife hızlı link + snapshot.
 * Sipariş ürünü tekliften türediği için items[] üstünde eşleşen kalem gösterilir.
 *
 * Props:
 *   order:  SupplierOrder (id, order_no, ...)
 *   contactMap: {id: Contact} (isim çözümü için)
 *   onClose(): void
 *   onChanged(): void — statü değişmediyse çağrılmaz
 */
export default function SupplierOrderDetailModal({ order, contactMap, onClose, onChanged }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(order?.notes || "");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotesDraft(order?.notes || "");
    const fetch = async () => {
      try {
        if (order?.quote_id) {
          const { data } = await api.get(`/price-quotes/${order.quote_id}`).catch(() => ({ data: null }));
          if (!cancelled) setQuote(data);
        } else if (!cancelled) {
          setQuote(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [order?.id, order?.quote_id]);

  const supplier = contactMap?.[order.supplier_contact_id];
  const customer = contactMap?.[order.customer_contact_id];

  // Teklifin items[]'inde bu siparişe karşılık gelen kalemi bulmaya çalış (flute/quality/qty eşleşmesi)
  const matchingItem = useMemo(() => {
    const items = quote?.items || [];
    if (!items.length) return null;
    const exact = items.find(
      (it) => String(it.flute || "").toUpperCase() === String(order.flute || "").toUpperCase()
           && String(it.quality || "").toUpperCase() === String(order.quality || "").toUpperCase()
           && Number(it.quantity || 0) === Number(order.quantity || 0)
    );
    return exact || items.find(
      (it) => String(it.flute || "").toUpperCase() === String(order.flute || "").toUpperCase()
           && String(it.quality || "").toUpperCase() === String(order.quality || "").toUpperCase()
    ) || null;
  }, [quote, order]);

  const perUnit = Number(order.quantity || 0) > 0
    ? Number(order.total_cost || 0) / Number(order.quantity)
    : Number(order.unit_cost || 0);

  // Kârlılık: matching item'in satış tutarını (karton_total, marjlı) yakala,
  // tedarikçi maliyetinden (order.total_cost) çıkararak kâr & marj yüzdesini üret.
  const profit = React.useMemo(() => {
    if (!matchingItem) return null;
    const sale = Number(matchingItem.karton_total || 0);
    const cost = Number(order.total_cost || 0);
    if (cost <= 0) return null;
    const amount = sale - cost;
    // Kârlılık: maliyet üzeri markup — Fiyat Hesaplama'daki marj ile aynı yapı.
    const markupPct = (amount / cost) * 100;
    return { sale, cost, amount, markupPct };
  }, [matchingItem, order.total_cost]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/supplier-orders/${order.id}`, { notes: notesDraft.trim() || null });
      toast.success("Not güncellendi");
      onChanged && onChanged();
    } catch (e) {
      toast.error("Güncellenemedi: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setSavingNotes(false);
    }
  };

  const copySnapshot = async () => {
    const lines = [
      `${order.order_no} · ${supplier?.name || "—"}`,
      `Ürün: ${order.flute || "—"} · ${order.quality || "—"} · ${order.box_type === "plaka" ? "Düz Plaka" : "A Box"}`,
      `Adet: ${order.quantity}  Birim: ${fmtTRY(perUnit)}  Toplam: ${fmtTRY(order.total_cost)}`,
      `Sipariş: ${fmtDate(order.date)}${order.delivery_date ? " · Teslim: " + fmtDate(order.delivery_date) : ""}`,
      order.notes ? `Not: ${order.notes}` : "",
    ].filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(lines);
      toast.success("Kopyalandı");
    } catch { toast.error("Kopyalanamadı"); }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="order-detail-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="order-detail-modal"
        className="bg-white border-2 border-black w-full max-w-3xl max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-4 border-b-2 border-black">
          <div>
            <h3 className="font-display text-lg font-bold flex items-center gap-2">
              <Package size={16} /> {order.order_no}
              <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ml-2 border ${
                order.status === "received" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                : order.status === "cancelled" ? "bg-neutral-200 text-neutral-600 border-neutral-300"
                : "bg-amber-100 text-amber-800 border-amber-300"
              }`}>
                {order.status === "received" ? "Teslim Alındı"
                 : order.status === "cancelled" ? "İptal" : "Açık"}
              </span>
            </h3>
            <div className="text-xs text-neutral-500 mt-0.5">
              Sipariş: {fmtDate(order.date)}
              {order.delivery_date && <> · Teslim planlı: {fmtDate(order.delivery_date)}</>}
            </div>
          </div>
          <button onClick={onClose} data-testid="order-detail-close" className="p-1"><X size={16} /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Cari kartları */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <InfoCard title="Tedarikçi" name={supplier?.name || "—"} sub={supplier?.tax_no || supplier?.phone || ""} />
            <InfoCard title="Müşteri (referans)" name={customer?.name || "—"} sub={customer?.tax_no || customer?.phone || ""} />
          </div>

          {/* Ürün Snapshot */}
          <div className="border-2 border-black/20 p-3">
            <div className="label-caps mb-2">Ürün Snapshot</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <F label="Karton">
                <span className="font-semibold">{order.flute || "—"}</span> · {order.quality || "—"}
              </F>
              <F label="Kutu Tipi">
                {order.box_type === "plaka" ? "Düz Plaka" : order.box_type === "abox" ? "A Box" : (order.box_type || "—")}
              </F>
              <F label="Adet">
                <span className="font-mono-num font-semibold">{Number(order.quantity || 0).toLocaleString("tr-TR")}</span>
              </F>
              <F label="Birim Maliyet">
                <span className="font-mono-num">{fmtTRY(perUnit)}</span>
              </F>
              {matchingItem && matchingItem.box_type === "plaka" && (
                <F label="Levha (mm)" mono>
                  {matchingItem.sheet_width_mm}×{matchingItem.sheet_length_mm}
                </F>
              )}
              {matchingItem && matchingItem.box_type !== "plaka" && matchingItem.length_mm != null && (
                <F label="Ölçü (mm)" mono>
                  {matchingItem.length_mm}×{matchingItem.width_mm}×{matchingItem.height_mm}
                </F>
              )}
              {matchingItem?.total_m2 && (
                <F label="Toplam m²" mono>
                  {Number(matchingItem.total_m2).toLocaleString("tr-TR", { maximumFractionDigits: 4 })} m²
                </F>
              )}
              {order.stock_sku && (
                <F label="Stok Kodu" mono>{order.stock_sku}</F>
              )}
              {(() => {
                const productName = (matchingItem?.title && matchingItem.title.trim())
                  || (matchingItem?.stock_name && matchingItem.stock_name.trim())
                  || (order.stock_name && order.stock_name.trim());
                return productName ? (
                  <div className="col-span-full">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ürün Adı</div>
                    <div className="font-semibold mt-0.5" data-testid="order-detail-product-name">{productName}</div>
                  </div>
                ) : null;
              })()}
            </div>
            <div className="mt-3 pt-3 border-t border-black/10 flex items-center justify-between">
              <div className="text-xs text-neutral-500">Toplam</div>
              <div className="font-mono-num text-lg font-bold text-emerald-700" data-testid="order-detail-total">
                {fmtTRY(order.total_cost)}
              </div>
            </div>
          </div>

          {/* Kârlılık Analizi — matching item bulunduysa gösterilir */}
          {profit && (
            <div
              className={`border-2 p-3 ${
                profit.amount > 0
                  ? "border-emerald-600/60 bg-emerald-50"
                  : profit.amount < 0
                    ? "border-rose-600/60 bg-rose-50"
                    : "border-black/20 bg-neutral-50"
              }`}
              data-testid="order-profit-panel"
            >
              <div className="label-caps mb-2 flex items-center gap-1.5">
                Kârlılık Analizi
                <span className="text-[10px] normal-case tracking-normal text-neutral-500">
                  · Teklif satışı vs. tedarikçi maliyeti
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <F label="Satış (Teklif)">
                  <span className="font-mono-num font-semibold">{fmtTRY(profit.sale)}</span>
                </F>
                <F label="Maliyet (Tedarikçi)">
                  <span className="font-mono-num text-neutral-700">{fmtTRY(profit.cost)}</span>
                </F>
                <F label="Kâr Tutarı">
                  <span className={`font-mono-num font-bold ${
                    profit.amount > 0 ? "text-emerald-700" : profit.amount < 0 ? "text-rose-700" : ""
                  }`} data-testid="order-profit-amount">
                    {fmtTRY(profit.amount)}
                  </span>
                </F>
                <F label="Kâr Marjı">
                  <span className={`font-mono-num font-bold ${
                    profit.amount > 0 ? "text-emerald-700" : profit.amount < 0 ? "text-rose-700" : ""
                  }`} data-testid="order-profit-margin">
                    {profit.markupPct >= 0 ? "+" : ""}%{profit.markupPct.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}
                  </span>
                  <span className="text-[10px] text-neutral-500 ml-1">(maliyet üzeri)</span>
                </F>
              </div>
              {profit.amount < 0 && (
                <div className="mt-2 text-[11px] text-rose-700">
                  ⚠ Bu tedarikçiyle satış tekliftin altında; sipariş zararına açılıyor.
                </div>
              )}
            </div>
          )}

          {/* Kaynak Teklif */}
          <div className="border border-black/10 p-3 bg-neutral-50">
            <div className="flex items-center justify-between mb-1.5">
              <div className="label-caps flex items-center gap-1.5"><FileText size={11} /> Kaynak Teklif</div>
              {order.quote_id && (
                <a
                  href={`/teklifler`}
                  data-testid="order-detail-quote-link"
                  className="text-xs underline inline-flex items-center gap-1 hover:text-emerald-700"
                >
                  Tekliflere git <ExternalLink size={11} />
                </a>
              )}
            </div>
            {loading ? (
              <div className="text-xs text-neutral-500">Yükleniyor…</div>
            ) : quote ? (
              <div className="text-xs space-y-0.5">
                <div>
                  <span className="text-neutral-500">Teklif tarihi:</span>{" "}
                  <span className="font-mono-num">{fmtDate(quote.date)}</span>
                  {quote.status === "won" && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest bg-emerald-700 text-white px-1.5 py-0.5">Olumlu</span>
                  )}
                </div>
                {quote.title && <div className="text-neutral-500 italic">"{quote.title}"</div>}
                <div className="text-neutral-500">
                  Teklif toplamı: <span className="font-mono-num text-neutral-800 font-semibold">{fmtTRY(quote.grand_total)}</span>
                  {" · "}Sepet: <span className="font-mono-num text-neutral-800">{(quote.items || []).length || 1} kalem</span>
                </div>
              </div>
            ) : (
              <div className="text-xs text-neutral-400">Kaynak teklif bulunamadı (elle açılmış sipariş olabilir).</div>
            )}
          </div>

          {/* Not düzenleme */}
          <div>
            <div className="label-caps mb-1.5">Notlar</div>
            <textarea
              rows={3}
              className="input font-normal"
              data-testid="order-detail-notes"
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              placeholder="Bu siparişe ek not, malzeme detayı, iletişim…"
            />
            <div className="flex items-center justify-end mt-2">
              <button
                onClick={saveNotes}
                disabled={savingNotes || (notesDraft || "").trim() === (order.notes || "")}
                data-testid="order-detail-save-notes"
                className="bg-black text-white px-3 py-1.5 text-xs uppercase tracking-widest disabled:opacity-40 flex items-center gap-1.5"
              >
                <Save size={12} /> Notu Kaydet
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between p-4 border-t-2 border-black bg-neutral-50">
          <button
            onClick={copySnapshot}
            data-testid="order-detail-copy"
            className="border border-black/30 px-3 py-2 text-xs uppercase tracking-widest hover:bg-white flex items-center gap-1.5"
          >
            <Copy size={12} /> Özeti Kopyala
          </button>
          <button onClick={onClose} className="bg-black text-white px-3 py-2 text-xs uppercase tracking-widest">Kapat</button>
        </div>

        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:8px 10px;font-size:13px;outline:none;background:white}.input:focus{border-color:black}`}</style>
      </div>
    </div>
  );
}

function F({ label, children, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`mt-0.5 ${mono ? "font-mono-num" : ""}`}>{children}</div>
    </div>
  );
}

function InfoCard({ title, name, sub }) {
  return (
    <div className="border border-black/10 p-3 bg-white">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1">
        <Truck size={10} /> {title}
      </div>
      <div className="mt-1 font-semibold">{name}</div>
      {sub && <div className="text-[10px] text-neutral-500 font-mono-num mt-0.5">{sub}</div>}
    </div>
  );
}
